import React, { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList, Multiselect } from "react-widgets";
import { toastr } from "react-redux-toastr";
import ErrorMessage from "../../../components/text/ErrorMessage";
import { CreateLead, Lead } from "../../../core/api/models";
import api from "../../../api";
import {
  useLead,
  useHasPermission,
  AppPermissions,
  CityLocation,
  City,
  CityZone,
} from "../../../core";
import { useMasterData } from "../../../core/hooks/useMasterData";
import { TextInput } from "../../../components/form-group";
import { decodeHtmlEntities } from "../../../utils/common";

const LeadEdit: React.FunctionComponent<{
  error?: string;
  leadDetails?: Lead;
  onUpdate?: () => void;
}> = (props) => {
  const { updateLead, updating, leadError } = useLead(api.lead);
  const masterData = useMasterData(api.settings);
  const [leadIntent, setLeadIntent] = React.useState<string>(
    props?.leadDetails?.leadIntent?._id || ""
  );
  const [type, setType] = React.useState<string>(
    props.leadDetails?.leadType?._id || ""
  );
  const [cities, setCities] = useState<any>([]);
  const [microMarketsData, setMicroMarketsData] = useState<any>([]);
  const [preferredLocationsData, setPreferredLocationsData] = useState<any>([]);
  const [projectType, setProjectType] = useState<any>([]);
  const [propertyType, setPropertyType] = useState<any>([]);

  const canUpdateLeadSettings =
    useHasPermission(AppPermissions.UpdateLeadSettings) &&
    props?.leadDetails?.isEditable;

  const { control, handleSubmit, errors, trigger, watch, setValue } = useForm<CreateLead>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const minArea = watch("minArea");
  const maxArea = watch("maxArea");
  const minPrice = watch("minPrice");
  const maxPrice = watch("maxPrice");

  useEffect(() => {
    trigger("maxPrice");
    trigger("minPrice");
    trigger("minArea");
    trigger("maxArea");
  }, [
    watch("minPrice"),
    watch("maxPrice"),
    watch("maxPrice"),
    watch("minArea"),
    watch("maxArea"),
  ])

  React.useEffect(() => {
    if (props.leadDetails?._id && props?.leadDetails?.cities) {
      handleCityZones(props?.leadDetails?.cities)
      handlePreferredLocationsChange(props?.leadDetails?.cities);
    }
  }, [props?.leadDetails?._id]);

  useEffect(() => {
    const fetchCityData = async () => {
      try {
        const cityData = await api.AddNewRequirement.loadAllCities();
        const cityOptions = cityData?.map((item: City) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.cityName),
        }));
        cityOptions.sort((a, b) => a.name.localeCompare(b.name));
        setCities(cityOptions);
      } catch (error) {
        // console.log("Failed to fetch city data:", error);
      }


    };
    handlePropertyType();
    handleProjectType();

    fetchCityData();
  }, []);

  const handleCityZones = async (selectedCityId: string) => {
    try {
      const microMarketsListData = await api.AddNewRequirement.getCityZones(
        selectedCityId
      );
      const cityZoneOptions = microMarketsListData?.map((item: CityZone) => ({
        value: item._id,
        name: decodeHtmlEntities(item?.cityZoneName),
      }));
      setMicroMarketsData(cityZoneOptions);
    } catch (error) {
      // console.log("Failed to fetch micro markets data:", error);
    }
  };

  const handlePreferredLocationsChange = async (selectedCityId: string) => {
    try {
      const preferredLocationsListData = await api.AddNewRequirement.getPreferedLocations(
        selectedCityId
      );
      const cityPreferedLocOptions = preferredLocationsListData?.map(
        (item: CityLocation) => ({
          value: item._id,
          name: decodeHtmlEntities(item?.cityLocName),
        })
      );
      setPreferredLocationsData(cityPreferedLocOptions);
    } catch (error) {
      // console.log("Failed to fetch preferred locations data:", error);
    }
  };

  const handlePropertyType = async () => {
    try {
      const propertyTypeData = await api.AddNewRequirement.getPropertyType();
      const subTypes = propertyTypeData.reduce((acc: any, item: any) => {
        acc.push(
          ...item.subTypes.map(
            (subType: { _id: any; propertyTypeName: any }) => ({
              value: subType._id,
              name: decodeHtmlEntities(subType.propertyTypeName),
            })
          )
        );
        return acc;
      }, []);
      setPropertyType(subTypes);
    } catch (error) {
      // console.log("Failed to fetch property type data:", error);
    }
  };

  const handleProjectType = async () => {
    try {
      const projectTypeData = await api.AddNewRequirement.getProjectType();
      const subTypes = projectTypeData.reduce((acc: any, item: any) => {
        if (item?.subTypes) {
          acc.push(
            ...item.subTypes.map(
              (subType: { _id: any; propertyTypeName: any }) => ({
                value: subType._id,
                name: decodeHtmlEntities(subType.propertyTypeName),
              })
            )
          );
        }
        return acc;
      }, []);
      setProjectType(subTypes);
    } catch (error) {
      // console.log("Failed to fetch project type data:", error);
    }
  };

  const onSubmit = async (leadData?: any) => {
    const updateData = {
      leadType: type,
      leadSource: leadData?.leadSource,
      leadIntent,
      // initialTouchPoint: leadData?.initialTouchPoint,
      propertyType: leadData?.propertyType?.value || leadData?.propertyType || undefined,
      decisionTimeframe:
        leadData?.decisionTimeframe?._id || leadData?.decisionTimeframe,
      title: leadData?.title,
      cities: leadData?.cities?.value || leadData?.cities || undefined,
      microMarkets: leadData?.microMarkets?.map(
        (item: any) => item?.value || item
      ) || undefined,
      preferredLocations: leadData?.preferredLocations?.map(
        (item: any) => item?.value || item
      ) || undefined,
      projectType: leadData?.projectType?.map((pType: any) => pType.value) || leadData?.projectType || undefined,
      minPrice: leadData?.minPrice || undefined,
      maxPrice: leadData?.maxPrice || undefined,
      minArea: leadData?.minArea || undefined,
      maxArea: leadData?.maxArea || undefined,
      weightage: leadData?.weightage || undefined,
      floorPreferences:
        leadData?.floorPreferences?.map((item: any) => item?.value || item) || undefined,
      preferedProjects:
        leadData?.preferedProjects?.map((item: any) => item?.value || item) || undefined,
      preferredDevelopers:
        leadData?.preferredDevelopers?.map((item: any) => item?.value || item) || undefined,
      note: leadData?.note || undefined,
    };
    try {
      updateLead(updateData, props?.leadDetails?._id, () => {
        toastr.success("Success", "Lead updated successfully");
        props.onUpdate?.();
      });
    } catch (error) {
      if (error?.response?.data?.data?.message) {
        toastr.error("Error", error?.response?.data?.data?.message.join());
      } else {
        toastr.error("Error", error?.response?.data?.message);
      }
    }
  };

  const leadIntentSelect = (vaue: string) => {
    setLeadIntent(vaue);
    setType('');
    setValue('leadType', '');
  };

  return (
    <div className="p-4 accordion__body border-bottom">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group font-weight-semibold mb-4">
          <label htmlFor="" className="font-sm text-uppercase">
            Title<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="title"
            defaultValue={props?.leadDetails?.title}
            render={({ onChange, value, name }) => (
              <input
                type="text"
                className="form-control rounded-sm font-weight-semibold"
                placeholder="Title"
                // name={name}
                value={value}
                onChange={onChange}
              // id="title"
              />
            )}
          />
          {errors.title && (
            <ErrorMessage>Title is required</ErrorMessage>
          )}
        </div>
        <div className="form-group font-weight-semibold mb-3">
          <label htmlFor="" className="font-sm text-uppercase">
            Lead Intent<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="leadIntent"
            defaultValue={props.leadDetails?.leadIntent?._id}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={masterData?.data?.leadIntents}
                textField={"name"}
                valueField={"_id"}
                placeholder={"Select Lead Intent"}
                onChange={(val) => {
                  onChange(val)
                  leadIntentSelect(val._id)
                }}
                autoFocus={true}
                disabled={!canUpdateLeadSettings}
              />
            )}
          />
          {errors.leadIntent && (
            <ErrorMessage>Lead Intent is required</ErrorMessage>
          )}
        </div>

        <div className="form-group font-weight-semibold mb-4">
          <label htmlFor="" className="font-sm text-uppercase">
            Property Type Interested<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="propertyType"
            defaultValue={props?.leadDetails?.propertyType}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={propertyType}
                textField={"name"}
                valueField={"value"}
                placeholder={"Select Property Type Interested"}
                onChange={onChange}
              />
            )}
          />
          {errors.propertyType && (
            <ErrorMessage>Property Type Interested is required</ErrorMessage>
          )}
        </div>

        <div className="form-group font-weight-semibold mb-4">
          <label htmlFor="" className="font-sm text-uppercase">
            Decision Timeframe<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="decisionTimeframe"
            defaultValue={props.leadDetails?.decisionTimeFrame?._id}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={masterData?.data?.decisionTimeFrames || []}
                textField={"name"}
                valueField={"_id"}
                placeholder={"Select Decision Timeframe"}
                onChange={onChange}
              />
            )}
          />
          {errors.decisionTimeframe && (
            <ErrorMessage>Decision Timeframe is required</ErrorMessage>
          )}
        </div>

        <Fragment>
          <div className="form-group font-weight-semibold mb-4">
            <label className="font-sm font-weight-semibold mb-1" htmlFor="cities">
              CITY:<span className="text-danger">*</span>
            </label>
            <Controller
              name="cities"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: "City is required",
                },
              }}
              defaultValue={props.leadDetails?.cities || ''}
              render={({ onChange, value }) => (
                <DropdownList
                  value={value}
                  data={cities}
                  busy={cities?.length === 0}
                  textField="name"
                  valueField={"value"}
                  filter="contains"
                  onChange={(val) => {
                    onChange(val);
                    handleCityZones(val?.value)
                    handlePreferredLocationsChange(val?.value);
                    setValue("preferredLocations", []);
                    setValue("microMarkets", []);
                  }}
                  placeholder="Select City"
                />
              )}
            />
            {errors.cities && <ErrorMessage>City is required</ErrorMessage>}
          </div>

          <div className="form-group font-weight-semibold mb-4">
            <label className="font-sm font-weight-semibold mb-1" htmlFor="microMarkets">
              MICRO MARKETS:
            </label>
            <Controller
              name="microMarkets"
              control={control}
              defaultValue={props.leadDetails?.microMarkets || ''}
              render={({ onChange, value }) => (
                <Multiselect
                  value={microMarketsData?.length ? value : []}
                  data={microMarketsData}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  placeholder="Search"
                />
              )}
            />
          </div>


          <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="preferredLocations"
            >
              PREFERRED LOCATIONS:
            </label>
            <Controller
              name="preferredLocations"
              control={control}
              defaultValue={props.leadDetails?.preferredLocations || null}
              render={({ onChange, value }) => (
                <Multiselect
                  value={preferredLocationsData?.length ? value : []}
                  data={preferredLocationsData}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  placeholder="Search Locations"
                />
              )}
            />
            {errors.preferredLocations && (
              <ErrorMessage>Preferred Locations is required</ErrorMessage>
            )}
          </div>
          <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="projectType"
            >
              PROJECT TYPE:
            </label>
            <Controller
              name="projectType"
              control={control}
              defaultValue={props.leadDetails?.projectType || ''}
              render={({ onChange, value }) => (
                <Multiselect
                  value={projectType?.length ? value : []}
                  data={projectType}
                  textField={"name"}
                  valueField={"value"}
                  onChange={(val) => {
                    onChange(val);
                  }}
                  placeholder="Search Project Types"
                />
              )}
            />
          </div>

          <div className="form-group font-weight-semibold mb-1">
            <label
              className="font-md font-weight-semibold mb-1"
              htmlFor=""
            >
              Select Budget(Per Sq. Ft.)
            </label>
          </div>
          <div className="row gutter-8">
            <div className="col-6  form-group font-weight-semibold textInput-component-style mb-4">
              <label
                className="font-sm text-uppercase mb-1"
                htmlFor="minPrice"
              >
                Min Price:
              </label>
              <Controller
                name="minPrice"
                control={control}
                defaultValue={props.leadDetails?.minPrice || ''}
                rules={{
                  validate: (value) => {
                    const numericMaxPrice = maxPrice;
                    if (
                      numericMaxPrice &&
                      (value === undefined || value === null || value === "")
                    ) {
                      return "Min Price is required";
                    }
                    return undefined;
                  },
                }}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Min Price"
                    onChange={onChange}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.minPrice && (
                <ErrorMessage>{errors.minPrice.message}</ErrorMessage>
              )}
            </div>
            <div className="col-6 form-group font-weight-semibold textInput-component-style mb-4">
              <label
                className="font-sm font-weight-semibold mb-1"
                htmlFor="maxPrice"
              >
                MAX PRICE:
              </label>

              <Controller
                name="maxPrice"
                control={control}
                rules={{
                  validate: (value) => {
                    const numericValue = value;
                    const tempMinPrice = minPrice;
                    return (tempMinPrice || tempMinPrice === 0) &&
                      numericValue !== 0 &&
                      !numericValue
                      ? "Max Price is required"
                      : (!numericValue && numericValue !== 0) ||
                      (!tempMinPrice && tempMinPrice !== 0) ||
                      (numericValue && numericValue > tempMinPrice) ||
                      (numericValue === 0 && tempMinPrice === 0) ||
                      "Max Price must be greater than Min Price";
                  },
                }}
                defaultValue={props.leadDetails?.maxPrice || ''}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Max Price"
                    onChange={onChange}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.maxPrice && (
                <ErrorMessage>{errors.maxPrice.message}</ErrorMessage>
              )}
            </div>
          </div>
          <div className="form-group font-weight-semibold mb-1">
            <label
              className="font-md font-weight-semibold mb-1"
              htmlFor=""
            >
              Area Offered(Sq. Ft.)
            </label>
          </div>
          <div className="row gutter-8">
            <div className="col-6 form-group font-weight-semibold textInput-component-style mb-4">
              <label
                className="font-sm text-uppercase"
                htmlFor="minPrice"
              >
                Min Area:
              </label>
              <Controller
                name="minArea"
                control={control}
                rules={{
                  validate: (value) => {
                    const numericMaxArea = maxArea;
                    if (
                      numericMaxArea &&
                      (value === undefined || value === null || value === "")
                    ) {
                      return "Min Area is required";
                    }
                    return undefined;
                  },
                }}
                defaultValue={props.leadDetails?.minArea || ''}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Min Area"
                    onChange={onChange}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.minArea && (
                <ErrorMessage>{errors.minArea.message}</ErrorMessage>
              )}
            </div>
            <div className="col-6 form-group font-weight-semibold textInput-component-style mb-4">
              <label
                className="font-sm text-uppercase"
                htmlFor="minPrice"
              >
                Max Area:
              </label>
              <Controller
                name="maxArea"
                control={control}
                rules={{
                  validate: (value) => {
                    const numericValue = value;
                    const tempMinArea = minArea;
                    return (tempMinArea || tempMinArea === 0) &&
                      numericValue !== 0 &&
                      !numericValue
                      ? "Max Area is required"
                      : (!numericValue && numericValue !== 0) ||
                      (!tempMinArea && tempMinArea !== 0) ||
                      (numericValue && numericValue > tempMinArea) ||
                      (numericValue === 0 && tempMinArea === 0) ||
                      "Max Area must be greater than Min Area";
                  },
                }}
                defaultValue={props.leadDetails?.maxArea || ''}
                render={({ onChange, value, name }) => (
                  <TextInput
                    type="number"
                    placeholder="Max Area"
                    onChange={onChange}
                    name={name}
                    default={value}
                    gap="no"
                    clearFeildValue={true}
                  />
                )}
              />
              {errors.maxArea && (
                <ErrorMessage>{errors.maxArea.message}</ErrorMessage>
              )}
            </div>
          </div>
          <div className="form-group font-weight-semibold mb-4">
            <label
              className="font-sm font-weight-semibold mb-1"
              htmlFor="preferredDevelopers"
            >
              REMARKS/NOTES:
            </label>
            <Controller
              control={control}
              name="note"
              defaultValue={props.leadDetails?.note || null}
              render={({ onChange, value, name }) => (
                <textarea
                  className="form-control rounded-sm font-weight-semibold h-auto"
                  placeholder="Comments"
                  name={name}
                  value={value}
                  onChange={onChange}
                  id="note"
                  rows={6}
                />
              )}
            />
            {errors.note && (
              <ErrorMessage>Remarks/Notes is required</ErrorMessage>
            )}
          </div>

          <div className="form-group font-weight-semibold mb-4">
          <label htmlFor="" className="font-sm text-uppercase">
            Lead Source<span className="text-danger">*</span>
          </label>
          <Controller
            control={control}
            rules={{ required: true }}
            name="leadSource"
            defaultValue={props.leadDetails?.leadSource?._id}
            render={({ onChange, value }) => (
              <DropdownList
                value={value}
                defaultValue={value}
                data={masterData?.data?.leadSources || []}
                textField={"name"}
                valueField={"_id"}
                placeholder={"Select Lead Source"}
                onChange={onChange}
              />
            )}
          />
          {errors.leadSource && (
            <ErrorMessage>Lead source is required</ErrorMessage>
          )}
        </div>
        </Fragment>

        <div className="mb-2 mt-2">
          {leadError && <span className="message_validation">{leadError}</span>}
        </div>
        <div className="form-group font-weight-semibold mb-3 text-right pt-3">
          <button
            className="btn btn-primary font-weight-semibold font-normal px-4 "
            disabled={!canUpdateLeadSettings || updating}
            title={
              !canUpdateLeadSettings
                ? "No permisssion to update Buying Influences"
                : ""
            }
          >
            Submit
          </button>
        </div>
      </form>
    </div>
  );
};

export default LeadEdit;
